import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  const token = getToken();
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': token } });
};

const STATUS_LIST = ['未开始', '进行中', '已完成', '已延期'];
const PRIORITY_LIST = ['高', '中', '低'];
const STATUS_CONFIG = {
  '未开始': { color: '#999', bg: '#f5f5f5' },
  '进行中': { color: '#1890ff', bg: '#e6f7ff' },
  '已完成': { color: '#52c41a', bg: '#f6ffed' },
  '已延期': { color: '#ff4d4f', bg: '#fff2f0' },
};
const PRIORITY_CONFIG = {
  '高': { color: '#ff4d4f', bg: '#fff2f0' },
  '中': { color: '#fa8c16', bg: '#fff7e6' },
  '低': { color: '#52c41a', bg: '#f6ffed' },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = localStorage.getItem('notes_user') || '';

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('wbs');
  const [loading, setLoading] = useState(true);
  const [editProject, setEditProject] = useState(false);
  const [projectForm, setProjectForm] = useState({});
  const [editTask, setEditTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', assignee: '', members: [], plannedStart: '', plannedEnd: '', priority: '中', progress: 0, warningDays: 3, warningTime: '09:00', noWarning: false, isRecurring: false, recurringRule: '', remarks: '', isPersonal: false, currentPeriod: 1, status: '未开始' });
  const ganttRef = useRef(null);
  const [ganttScale, setGanttScale] = useState(1);
  const [users, setUsers] = useState([]);

  useEffect(() => { fetchProject(); fetchUsers(); }, [id]);

  const fetchUsers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/auth/users`);
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
    } catch (e) { console.error(e); }
  };

  const fetchProject = async () => {
    setLoading(true);
    try {
      const [projRes, taskRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/projects/${id}`),
        fetchWithAuth(`${API_URL}/api/projects/${id}/tasks`),
      ]);
      const projData = await projRes.json();
      const taskData = await taskRes.json();
      setProject(projData);
      setTasks(taskData);
      setProjectForm(projData);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const flattenTasks = (taskList) => {
    const result = [];
    const flatten = (list, depth = 0) => {
      list.forEach(t => {
        result.push({ ...t, depth });
        if (t.children) flatten(t.children, depth + 1);
      });
    };
    flatten(taskList);
    return result;
  };

  const updateProject = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth(`${API_URL}/api/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectForm),
      });
      const data = await res.json();
      if (data.error) return alert(data.error);
      setProject(data);
      setEditProject(false);
    } catch (e) { alert('更新失败'); }
  };

  const deleteProject = async () => {
    if (!confirm('确定删除此项目及其所有任务？此操作不可恢复。')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/projects/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) return alert(data.error);
      navigate('/');
    } catch (e) { alert('删除失败'); }
  };

  const deleteTask = async (taskId) => {
    if (!confirm('确定删除此任务及其所有子任务？')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/tasks/${taskId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) return alert(data.error);
      fetchProject();
    } catch (e) { alert('删除失败'); }
  };

  // 任务重新排序（上移/下移/置顶/置底）
  const reorderTask = async (taskId, direction) => {
    // direction: -1 = 上移, 1 = 下移, -99 = 置顶, 99 = 置底
    const flatTasks = flattenTasks(tasks);
    const task = flatTasks.find(t => t.id === taskId);
    if (!task) return;

    // 获取同级任务
    const siblings = flatTasks.filter(t => t.parentId === task.parentId && t.level === task.level);
    const currentIndex = siblings.findIndex(t => t.id === taskId);
    
    let orderedIds = siblings.map(t => t.id);
    
    if (direction === -99) { // 置顶
      if (currentIndex === 0) return;
      orderedIds.splice(currentIndex, 1);
      orderedIds.unshift(taskId);
    } else if (direction === 99) { // 置底
      if (currentIndex === siblings.length - 1) return;
      orderedIds.splice(currentIndex, 1);
      orderedIds.push(taskId);
    } else {
      if (direction === -1 && currentIndex === 0) return;
      if (direction === 1 && currentIndex === siblings.length - 1) return;
      const newIndex = currentIndex + direction;
      [orderedIds[currentIndex], orderedIds[newIndex]] = [orderedIds[newIndex], orderedIds[currentIndex]];
    }

    try {
      const res = await fetchWithAuth(`/api/projects/${id}/tasks/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds }),
      });
      const data = await res.json();
      if (data.success) {
        fetchProject(); // 重新加载任务列表
      } else {
        alert(data.error || '排序失败');
      }
    } catch (e) { alert('排序失败'); }
  };

  const updateTask = async (e) => {
    e.preventDefault();
    if (!editTask) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/tasks/${editTask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm),
      });
      const data = await res.json();
      if (data.error) return alert(data.error);
      setEditTask(null);
      setTaskForm({ name: '', assignee: '', members: [], plannedStart: '', plannedEnd: '', priority: '中', progress: 0, warningDays: 3, warningTime: '09:00', noWarning: false, isRecurring: false, recurringRule: '', remarks: '', isPersonal: false, currentPeriod: 1, status: '未开始' });
      fetchProject();
    } catch (e) { alert('更新失败'); }
  };

  if (loading) return <div className="loading-state">加载中...</div>;
  if (!project) return <div className="empty-state">项目不存在</div>;

  const isOwner = project.owner === currentUser || currentUser === 'jch';
  const flatTasks = flattenTasks(tasks);
  const projectType = project.type || '实施';

  return (
    <div className="page-container">
      <div className="project-header">
        <div className="project-info">
          <h1>{project.name}</h1>
          <span className="project-type">{project.type || '实施'}</span>
          <span>负责人：{users.find(u => u.username === project.owner)?.realname || project.owner}</span>
          <span>创建于：{new Date(project.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="project-actions">
          <button className="btn-secondary" onClick={() => navigate(`/projects/${id}/board`)}>📊 项目看板</button>
          <button className="btn-secondary" onClick={() => navigate(`/projects/${id}/task-overview`)}>📋 任务概览</button>
          {isOwner && (
            <>
              <button className="btn-secondary" onClick={() => setEditProject(true)}>编辑</button>
              <button className="btn-danger" onClick={deleteProject}>删除</button>
            </>
          )}
        </div>
      </div>

      {/* 主Tab：项目维护 | 已完成任务维护 | 项目看板 | 任务概览 */}
      <div className="project-tabs" style={{ marginBottom: 16 }}>
        <button className="tab active" onClick={() => {}}>📝 项目维护</button>
        <button className="tab" onClick={() => navigate(`/projects/${id}/completed-tasks`)}>✅ 已完成任务维护</button>
        <button className="tab" onClick={() => navigate(`/projects/${id}/board`)}>📊 项目看板</button>
        <button className="tab" onClick={() => navigate(`/projects/${id}/task-overview`)}>📋 任务概览</button>
      </div>

      <div className="project-tabs" style={{ marginBottom: 16 }}>
        <button className={tab === 'wbs' ? 'tab active' : 'tab'} onClick={() => setTab('wbs')}>WBS分解</button>
        <button className={tab === 'gantt' ? 'tab active' : 'tab'} onClick={() => setTab('gantt')}>甘特图</button>
      </div>

      <div className="project-progress" style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span>项目进度</span>
          <span>{project.progress || 0}%</span>
        </div>
        <div style={{ height: 8, background: '#e8e8e8', borderRadius: 4 }}>
          <div style={{ height: '100%', width: `${project.progress || 0}%`, background: '#1890ff', borderRadius: 4 }} />
        </div>
      </div>

      {tab === 'wbs' && (
        <div className="wbs-section">
          <div className="wbs-header">
            <h2>WBS分解</h2>
            <button className="btn-primary btn-sm" onClick={() => navigate(`/projects/${id}/tasks/new`)}>+ 添加阶段</button>
          </div>
          <div className="wbs-tree">
            {tasks.length === 0 ? (
              <div className="empty-state-sm">暂无任务，点击上方按钮添加</div>
            ) : (
              tasks.map((t, idx) => (
                <WBSTreeNode
                  key={t.id}
                  task={t}
                  level={1}
                  index={idx}
                  totalSiblings={tasks.length}
                  isOwner={isOwner}
                  currentUser={currentUser}
                  projectType={projectType}
                  users={users}
                  onEdit={(task) => {
                    setEditTask(task);
                    setTaskForm({
                      name: task.name,
                      assignee: task.assignee || '',
                      members: task.members || [],
                      plannedStart: task.plannedStart?.split('T')[0] || '',
                      plannedEnd: task.plannedEnd?.split('T')[0] || '',
                      priority: task.priority || '中',
                      progress: task.progress || 0,
                      warningDays: task.warningDays ?? 3,
                      warningTime: task.warningTime || '09:00',
                      noWarning: task.noWarning || false,
                      isRecurring: task.isRecurring || false,
                      recurringRule: task.recurringRule || '',
                      remarks: task.remarks || '',
                      isPersonal: task.isPersonal || false,
                      currentPeriod: task.currentPeriod || 1,
                      status: task.status || '未开始',
                    });
                  }}
                  onDelete={deleteTask}
                  onAddChild={(parent) => {
                    if (parent.level >= 3) return alert('最多支持3级任务');
                    navigate(`/projects/${id}/tasks/new?parentId=${parent.id}&level=${parent.level + 1}`);
                  }}
                  onReorder={reorderTask}
                />
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'gantt' && (
        <div className="gantt-section">
          <div className="gantt-header">
            <h2>甘特图</h2>
            <div className="gantt-controls">
              <button onClick={() => setGanttScale(Math.max(0.5, ganttScale - 0.25))}>缩小</button>
              <span>{Math.round(ganttScale * 100)}%</span>
              <button onClick={() => setGanttScale(Math.min(3, ganttScale + 0.25))}>放大</button>
            </div>
          </div>
          <GanttChart tasks={flatTasks} scale={ganttScale} projectType={projectType} />
        </div>
      )}

      {editProject && (
        <div className="modal-overlay" onClick={() => setEditProject(false)}>
          <div className="modal-box modal-box-md" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">编辑项目</h3>
            <form onSubmit={updateProject}>
              <div className="form-group">
                <label>项目名称</label>
                <input type="text" value={projectForm.name || ''} onChange={e => setProjectForm({ ...projectForm, name: e.target.value })} className="form-input" required />
              </div>
              <div className="form-group">
                <label>项目类型</label>
                <select value={projectForm.type || '实施'} onChange={e => setProjectForm({ ...projectForm, type: e.target.value })} className="form-input">
                  <option value="实施">实施</option>
                  <option value="运维">运维</option>
                </select>
              </div>
              <div className="form-group">
                <label>项目状态</label>
                <select value={projectForm.status || '规划中'} onChange={e => setProjectForm({ ...projectForm, status: e.target.value })} className="form-input">
                  <option value="规划中">规划中</option>
                  <option value="进行中">进行中</option>
                  <option value="已完成">已完成</option>
                  <option value="已暂停">已暂停</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditProject(false)}>取消</button>
                <button type="submit" className="btn-primary">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTask && (
        <div className="modal-overlay" onClick={() => setEditTask(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <h3 className="modal-title">编辑任务</h3>
            <form onSubmit={updateTask}>
              <div className="form-group">
                <label>任务名称</label>
                <input type="text" value={taskForm.name} onChange={e => setTaskForm({ ...taskForm, name: e.target.value })} className="form-input" />
              </div>
              <div className="form-group">
                <label>备注</label>
                <textarea value={taskForm.remarks || ''} onChange={e => setTaskForm({ ...taskForm, remarks: e.target.value })} className="form-input" rows={2} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>负责人</label>
                  <select value={taskForm.assignee} onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })} className="form-input">
                    {users.map(u => <option key={u.username} value={u.username}>{u.realname}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>优先级</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })} className="form-input">
                    {PRIORITY_LIST.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>计划开始</label>
                  <input type="date" value={taskForm.plannedStart} onChange={e => setTaskForm({ ...taskForm, plannedStart: e.target.value })} className="form-input" />
                </div>
                <div className="form-group">
                  <label>计划结束</label>
                  <input type="date" value={taskForm.plannedEnd} onChange={e => setTaskForm({ ...taskForm, plannedEnd: e.target.value })} className="form-input" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>状态</label>
                  <select value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value })} className="form-input">
                    {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>进度 (%)</label>
                  <input type="number" min="0" max="100" value={taskForm.progress} onChange={e => setTaskForm({ ...taskForm, progress: Number(e.target.value) })} className="form-input" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditTask(null)}>取消</button>
                <button type="submit" className="btn-primary">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function WBSTreeNode({ task, level, index, totalSiblings, isOwner, currentUser, projectType, users, onEdit, onDelete, onAddChild, onReorder }) {
  const [expanded, setExpanded] = useState(true);
  const canEdit = isOwner || task.assignee === currentUser;
  const hasChildren = task.children && task.children.length > 0;
  const statusStyle = STATUS_CONFIG[task.status] || STATUS_CONFIG['未开始'];
  const priorityStyle = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG['中'];

  return (
    <div className="wbs-node" style={{ marginLeft: (level - 1) * 24 }}>
      <div className="wbs-node-main">
        <div className="wbs-node-left">
          {hasChildren && (
            <button className="btn-icon" onClick={() => setExpanded(!expanded)}>{expanded ? '▼' : '▶'}</button>
          )}
          {!hasChildren && <span style={{ width: 24 }} />}

          <span className="wbs-node-name">{task.name}</span>
          {/* 排序按钮：移到任务名后面 */}
          {canEdit && onReorder && (
            <span className="wbs-sort-btns" style={{ display: 'inline-flex', gap: 2, marginLeft: 6 }}>
              <button 
                onClick={() => onReorder(task.id, -1)}
                disabled={index === 0}
                title="上移"
                style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', padding: '0 4px', lineHeight: '20px', fontSize: 12, opacity: index === 0 ? 0.3 : 1 }}
              >↑</button>
              <button 
                onClick={() => onReorder(task.id, 1)}
                disabled={index === totalSiblings - 1}
                title="下移"
                style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', padding: '0 4px', lineHeight: '20px', fontSize: 12, opacity: index === totalSiblings - 1 ? 0.3 : 1 }}
              >↓</button>
              <button 
                onClick={() => onReorder(task.id, -99)}
                disabled={index === 0}
                title="置顶"
                style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', padding: '0 4px', lineHeight: '20px', fontSize: 12, opacity: index === 0 ? 0.3 : 1 }}
              >⤒</button>
              <button 
                onClick={() => onReorder(task.id, 99)}
                disabled={index === totalSiblings - 1}
                title="置底"
                style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', padding: '0 4px', lineHeight: '20px', fontSize: 12, opacity: index === totalSiblings - 1 ? 0.3 : 1 }}
              >⤓</button>
            </span>
          )}
          <span className="wbs-node-status" style={{ color: statusStyle.color, background: statusStyle.bg }}>{task.status}</span>
          <span className="wbs-node-priority" style={{ color: priorityStyle.color, background: priorityStyle.bg }}>{task.priority}</span>
          {task.isRecurring && <span style={{ padding: '2px 6px', background: '#f0f5ff', color: '#2f54eb', borderRadius: 4, fontSize: 12 }}>🔄</span>}
          {task.assignee && <span className="wbs-node-assignee">{users.find(u => u.username === task.assignee)?.realname || task.assignee}</span>}
          {task.plannedEnd && <span className="wbs-node-date">{task.plannedEnd?.split('T')[0]}</span>}
        </div>
        <div className="wbs-node-actions">
          {canEdit && (
            <>
              <button className="btn-link" onClick={() => onEdit(task)}>编辑</button>
              {level < 3 && (
                <button className="btn-link" onClick={() => onAddChild(task)}>添加子任务</button>
              )}
              <button className="btn-link" onClick={() => window.location.href = `/tasks/${task.id}`}>详情</button>
              <button className="btn-link" style={{ color: '#ff4d4f' }} onClick={() => onDelete(task.id)}>删除</button>
            </>
          )}
        </div>
      </div>
      {expanded && hasChildren && (
        <div className="wbs-children">
          {task.children.map((child, childIdx) => (
            <WBSTreeNode key={child.id} task={child} level={level + 1} index={childIdx} totalSiblings={task.children.length} isOwner={isOwner} currentUser={currentUser} projectType={projectType} users={users} onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} onReorder={onReorder} />
          ))}
        </div>
      )}
    </div>
  );
}

function GanttChart({ tasks, scale, projectType }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || tasks.length === 0) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rowHeight = 32;
    const leftPadding = 180;
    const topPadding = 40;
    const dayWidth = 30 * scale;

    const tasksWithDates = tasks.filter(t => t.plannedStart && t.plannedEnd);
    if (tasksWithDates.length === 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const allDates = tasksWithDates.flatMap(t => [new Date(t.plannedStart), new Date(t.plannedEnd)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 7);
    const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));

    canvas.width = (leftPadding + totalDays * dayWidth) * dpr;
    canvas.height = (topPadding + tasksWithDates.length * rowHeight) * dpr;
    canvas.style.width = `${leftPadding + totalDays * dayWidth}px`;
    canvas.style.height = `${topPadding + tasksWithDates.length * rowHeight}px`;
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    // 绘制日期标题
    ctx.fillStyle = '#666';
    ctx.font = '12px sans-serif';
    for (let i = 0; i < totalDays; i++) {
      const date = new Date(minDate);
      date.setDate(date.getDate() + i);
      const x = leftPadding + i * dayWidth;
      if (i % 7 === 0) {
        ctx.fillText(`${date.getMonth() + 1}/${date.getDate()}`, x + 4, 16);
        ctx.strokeStyle = '#f0f0f0';
        ctx.beginPath();
        ctx.moveTo(x, topPadding);
        ctx.lineTo(x, canvas.height / dpr);
        ctx.stroke();
      }
    }

    // 绘制任务条
    tasksWithDates.forEach((task, i) => {
      const y = topPadding + i * rowHeight + 8;
      const startDate = new Date(task.plannedStart);
      const endDate = new Date(task.plannedEnd);
      const startOffset = Math.floor((startDate - minDate) / (1000 * 60 * 60 * 24));
      const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      // 任务名称
      ctx.fillStyle = '#333';
      ctx.font = '12px sans-serif';
      ctx.fillText(task.name.substring(0, 20), 4, y + 14);

      // 任务条
      const statusStyle = STATUS_CONFIG[task.status] || STATUS_CONFIG['未开始'];
      ctx.fillStyle = statusStyle.color;
      ctx.fillRect(leftPadding + startOffset * dayWidth, y, duration * dayWidth - 4, rowHeight - 8);

      // 进度
      if (task.progress > 0) {
        ctx.fillStyle = '#52c41a';
        ctx.fillRect(leftPadding + startOffset * dayWidth, y, (duration * dayWidth - 4) * task.progress / 100, rowHeight - 8);
      }
    });

    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, leftPadding, canvas.height / dpr);
    ctx.strokeStyle = '#d9d9d9';
    ctx.beginPath();
    ctx.moveTo(leftPadding, 0);
    ctx.lineTo(leftPadding, canvas.height / dpr);
    ctx.stroke();
  }, [tasks, scale, projectType]);

  return (
    <div className="gantt-scroll" ref={containerRef}>
      <canvas ref={canvasRef} />
    </div>
  );
}
